import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { getPricingDetails } from "../../redux/user/userSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import { PayoutAnalyticsDashboard } from "../../components/Pricing/PayoutAnalyticsDashboard";
import { OFF_WHITE, PROFILE_FONT } from "../../constants/layout";

const holdingTypeMap: Record<number, string> = {
  1: "Bond",
  2: "Mutual Fund",
  3: "Fixed Deposit",
  4: "Equity",
  5: "AIF",
  6: "PMS",
  7: "PPF",
  8: "Unlisted Equity",
  9: "NPS",
};

const Analytics = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const userType = localStorage?.getItem("userType");

  const [loading, setLoading] = useState(true);
  const [top20, setTop20] = useState<Array<{ name: string; value: number }>>([]);
  const [byAsset, setByAsset] = useState<Array<{ name: string; value: number }>>([]);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>(() => ({
    startDate: dayjs().subtract(1, "year").startOf("day").valueOf(),
    endDate: dayjs().endOf("day").valueOf(),
  }));

  useEffect(() => {
    if (userType !== "4") {
      navigate("/dashboard", { replace: true });
    }
  }, [userType, navigate]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const payload = {
        partnerId: -1,
        holdingType: "-1",
        startDate: dateRange?.startDate || 0,
        endDate: dateRange?.endDate || 0,
        pageIndex: 1,
        pageSize: 1000,
      };

      try {
        const response = await dispatch(getPricingDetails(payload));
        const newData = response?.payload?.data || [];

        if (Array.isArray(newData)) {
          const assetMap: Record<string, number> = {};
          newData.forEach((item) => {
            const asset = holdingTypeMap[item.holdingType] || "Unknown";
            assetMap[asset] = (assetMap[asset] || 0) + (item.brockerage || 0);
          });
          setByAsset(Object.entries(assetMap).map(([name, value]) => ({ name, value })));

          const panMap: Record<string, { name: string; value: number }> = {};
          newData.forEach((item) => {
            const name = item.firstName?.trim();
            const pan = item.panCard?.trim();
            if (!name || !pan) return;

            if (!panMap[pan]) {
              panMap[pan] = { name, value: 0 };
            }
            panMap[pan].value += item.amountToPartner || 0;
          });

          const sorted = Object.values(panMap)
            .map((entry) => ({
              name: entry.name,
              value: Math.round(entry.value * 100) / 100,
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 20);

          setTop20(sorted);
        } else {
          setByAsset([]);
          setTop20([]);
        }
      } catch (error) {
        console.error("API Error:", error);
        setByAsset([]);
        setTop20([]);
      } finally {
        setLoading(false);
      }
    }

    if (userType === "4") {
      fetchData();
    }
  }, [dispatch, userType, dateRange]);

  if (userType !== "4") {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={loading} />

      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <PayoutAnalyticsDashboard
          top20={top20}
          byAsset={byAsset}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      </Box>
    </Box>
  );
};

export default Analytics;
